import * as React from "react";

import { PageLayout } from "@/shared/components";
import { DevicesList, DevicesTabs } from "@/shared/components/devices";
import { CreateDeviceButton } from "@/shared/components/devices/CreateDevice";
import { type SelectionOption } from "@/shared/components/ResourceView";
import { type Device } from "@/shared/domain/devices";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import {
  CategoryOutlinedIcon,
  DeleteOutlineIcon,
} from "@/shared/foundation/icons/mui";

import { DeleteDeviceDialog } from "../DeviceDetails/DeleteDeviceDialog";

import styles from "./Devices.module.css";
import { SetDeviceTypeDialog } from "./SetDeviceTypeDialog";

/** The devices a bulk action targets, plus the callback that clears the grid's selection. */
interface PendingAction {
  devices: Device[];
  clearSelection: () => void;
}

export const Devices: React.FC = () => {
  const [pendingDelete, setPendingDelete] =
    React.useState<PendingAction | null>(null);
  const [pendingTypeChange, setPendingTypeChange] =
    React.useState<PendingAction | null>(null);
  const assetStoreEnabled = useCheckFeatureFlag("release-asset-store");

  const selectionOptions: SelectionOption<Device>[] = React.useMemo(
    () => [
      ...(assetStoreEnabled
        ? [
            {
              name: "Set device type…",
              icon: <CategoryOutlinedIcon fontSize="small" />,
              action: ({ selected, clearSelection }) => {
                setPendingTypeChange({ devices: selected, clearSelection });
              },
            } satisfies SelectionOption<Device>,
          ]
        : []),
      {
        name: "Delete devices",
        icon: <DeleteOutlineIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          setPendingDelete({ devices: selected, clearSelection });
        },
      },
    ],
    [assetStoreEnabled],
  );

  return (
    <PageLayout title={"Roboto - Devices"} headerContentLeft={"Devices"}>
      <div className={styles.content}>
        <DevicesTabs activeTab="devices" />
        <div className={styles.table}>
          <DevicesList
            selectionOptions={selectionOptions}
            actions={<CreateDeviceButton />}
          />
        </div>
      </div>
      <SetDeviceTypeDialog
        devices={pendingTypeChange?.devices ?? []}
        onClose={() => setPendingTypeChange(null)}
        onChanged={(failed) => {
          // Clear the grid's selection even after a partial change: the dialog
          // keeps the failed devices for a retry.
          pendingTypeChange?.clearSelection();
          if (failed.length === 0) {
            setPendingTypeChange(null);
          }
        }}
      />
      <DeleteDeviceDialog
        devices={pendingDelete?.devices ?? []}
        onClose={() => setPendingDelete(null)}
        onDeleted={(failed) => {
          // Clear even after a partial delete: the deleted devices no longer
          // exist, and the dialog keeps the failed ones for a retry.
          pendingDelete?.clearSelection();
          if (failed.length === 0) {
            setPendingDelete(null);
          }
        }}
      />
    </PageLayout>
  );
};
